import React, { useCallback, useEffect, useState } from 'react';
import { FileDown, Loader2, Sparkles, Trash2, Download, X, Wand2, AlertTriangle, Send, Check } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { getMountedWhiteboardEditors } from '@/components/classroom/MultiBoardPanel';
import SendRecapModal from '@/components/classroom/SendRecapModal';
import { toast } from 'sonner';

/**
 * Phase 3A-3: Lesson Recap export panel.
 *
 *  - Tutor: optional summary + lesson_track selector → click "Generate Recap"
 *    → captures all currently-mounted whiteboards as PNGs via tldraw
 *    editor.toImage(), POSTs to /api/classroom/room/.../recap/generate.
 *  - Both roles: list of past recaps with Download buttons.
 *  - Tutor: can Delete a recap (removes Cloudinary asset + DB row).
 */
const TRACKS = [
  { key: 'IELTS Writing', label: 'IELTS Writing' },
  { key: 'IELTS Speaking', label: 'IELTS Speaking' },
  { key: 'GMAT Quant', label: 'GMAT Quant' },
  { key: 'GMAT Verbal', label: 'GMAT Verbal' },
  { key: 'CBC', label: 'CBC' },
  { key: 'Coding', label: 'Coding / Robotics' },
  { key: '', label: 'General' },
];

async function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

async function captureWhiteboards(boards) {
  const editors = getMountedWhiteboardEditors();
  const out = [];
  for (const b of boards) {
    if (b.type !== 'whiteboard') {
      // Non-whiteboard boards still get listed but without a snapshot
      out.push({ board_id: b.board_id, name: b.name, type: b.type, source_url: b.source_url || null, snapshot_png_b64: null });
      continue;
    }
    const editor = editors.get(b.board_id);
    let pngDataUrl = null;
    if (editor) {
      try {
        const shapeIds = Array.from(editor.getCurrentPageShapeIds?.() || []);
        if (shapeIds.length > 0) {
          // tldraw v2 editor.toImage returns { blob, width, height }
          const r = await editor.toImage(shapeIds, { format: 'png', background: true, padding: 24, scale: 1 });
          const blob = r?.blob || r;  // some versions return blob directly
          if (blob) pngDataUrl = await blobToDataURL(blob);
        }
      } catch (_e) {
        // swallow — empty/failed export is acceptable
      }
    }
    out.push({ board_id: b.board_id, name: b.name, type: b.type, snapshot_png_b64: pngDataUrl });
  }
  return out;
}

const RecapPanel = ({ roomId, token, role }) => {
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [draftSummary, setDraftSummary] = useState('');
  const [aiHomework, setAiHomework] = useState('');
  const [aiRevision, setAiRevision] = useState('');
  const [aiParent, setAiParent] = useState('');
  const [aiNextPlan, setAiNextPlan] = useState('');
  const [contextWarning, setContextWarning] = useState('');
  const [aiMeta, setAiMeta] = useState(null); // {model, generated_at}
  const [touched, setTouched] = useState({});  // track what tutor edited after AI fill
  const [track, setTrack] = useState('');
  const [sendingRecap, setSendingRecap] = useState(null);  // recap object when modal open

  const isTutor = role === 'tutor';

  const refresh = useCallback(async () => {
    try {
      const r = await classroomApi.recapsList(roomId, token);
      setRecaps(r.items || []);
    } catch (_e) {}
    finally { setLoading(false); }
  }, [roomId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const resetForm = () => {
    setDraftSummary('');
    setAiHomework(''); setAiRevision(''); setAiParent(''); setAiNextPlan('');
    setContextWarning(''); setAiMeta(null); setTouched({});
  };

  const closeModal = () => {
    if (generating || aiBusy) return;
    setOpen(false);
    resetForm();
  };

  const runAI = async () => {
    setAiBusy(true);
    setContextWarning('');
    try {
      const r = await classroomApi.recapAIDraft(roomId, token, { lesson_track: track || null });
      // Only populate fields the tutor hasn't already touched
      if (!touched.summary) setDraftSummary(r.tutor_summary || '');
      if (!touched.homework) setAiHomework(r.ai_homework || '');
      if (!touched.revision) setAiRevision(r.ai_revision_notes || '');
      if (!touched.parent) setAiParent(r.ai_parent_report || '');
      if (!touched.next) setAiNextPlan(r.ai_next_lesson_plan || '');
      setContextWarning(r.context_warning || '');
      setAiMeta({ model: r.model, generated_at: r.generated_at, context_strength: r.context_strength });
      toast.success(r.context_warning ? 'AI draft ready — please review carefully' : 'Winhouse AI draft ready');
    } catch (e) {
      toast.error(e.message || 'Winhouse AI is temporarily unavailable. You can still write the recap manually.');
    } finally {
      setAiBusy(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const list = await classroomApi.boardsList(roomId, token);
      const boards = list.items || [];
      const captured = await captureWhiteboards(boards);
      const tutorEdited = Object.values(touched).some(Boolean);
      const payload = {
        lesson_track: track || null,
        tutor_summary: draftSummary.trim() || null,
        boards: captured,
        ai_homework: aiHomework.trim() || null,
        ai_revision_notes: aiRevision.trim() || null,
        ai_parent_report: aiParent.trim() || null,
        ai_next_lesson_plan: aiNextPlan.trim() || null,
        ai_generated_at: aiMeta?.generated_at || null,
        ai_model_used: aiMeta?.model || null,
        tutor_edited: aiMeta ? tutorEdited : null,
      };
      const r = await classroomApi.recapGenerate(roomId, token, payload);
      toast.success('Lesson recap generated');
      setRecaps((rs) => [r, ...rs]);
      setOpen(false);
      resetForm();
    } catch (e) {
      toast.error(e.message || 'Could not build recap');
    } finally {
      setGenerating(false);
    }
  };

  const download = async (recap) => {
    try {
      // If we already have a secure_url on the row, just open it directly
      if (recap.secure_url) {
        window.open(recap.secure_url, '_blank');
        return;
      }
      const r = await classroomApi.recapDownload(roomId, token, recap.recap_id);
      if (r?.secure_url || r?.redirect) {
        window.open(r.secure_url || r.redirect, '_blank');
      } else {
        toast.error('Download link not available');
      }
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  const remove = async (recap) => {
    if (!window.confirm('Delete this recap permanently?')) return;
    try {
      await classroomApi.recapDelete(roomId, token, recap.recap_id);
      setRecaps((rs) => rs.filter((x) => x.recap_id !== recap.recap_id));
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="classroom-recap">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileDown className="h-4 w-4 text-red-700" />
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Lesson Recap</div>
          {recaps.length > 0 && (
            <span className="text-[10px] bg-red-700 text-white rounded-full px-2 py-0.5 font-bold">{recaps.length}</span>
          )}
        </div>
        {isTutor && (
          <button onClick={() => setOpen(true)}
            className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
            data-testid="recap-generate-open">
            <Sparkles className="h-3.5 w-3.5" /> Generate Recap
          </button>
        )}
      </div>

      <div className="p-4">
        {loading && <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>}
        {!loading && recaps.length === 0 && (
          <div className="text-[11px] text-gray-400 italic text-center py-6" data-testid="recap-empty">
            {isTutor ? 'No recap generated yet. Click Generate Recap to build one.' : 'Your tutor hasn\'t generated a recap for this class yet.'}
          </div>
        )}
        <div className="space-y-2" data-testid="recap-list">
          {recaps.map((r) => (
            <div key={r.recap_id}
                 className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg group hover:border-red-200 transition"
                 data-testid={`recap-row-${r.recap_id}`}>
              <div className="w-9 h-9 rounded-lg bg-black text-[#D3D86F] flex items-center justify-center flex-shrink-0">
                <FileDown className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-gray-900 truncate flex items-center gap-1.5">
                  {r.filename}
                  {r.sections_used?.ai && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-black text-[#D3D86F] text-[9px] font-bold uppercase tracking-wider" data-testid={`recap-ai-badge-${r.recap_id}`}>
                      <Sparkles className="h-2.5 w-2.5" /> AI
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider truncate">
                  {(r.lesson_track || r.subject || 'General') + ' · ' + new Date(r.generated_at).toLocaleString()}
                </div>
                {r.sections_used && (
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {r.sections_used.boards} board{r.sections_used.boards === 1 ? '' : 's'} ·
                    {' '}{r.sections_used.math_notes} math ·
                    {' '}{r.sections_used.chat} chat ·
                    {' '}{r.sections_used.documents} doc{r.sections_used.documents === 1 ? '' : 's'}
                    {r.sections_used.ai ? ' · AI sections' : ''}
                  </div>
                )}
                {r.delivery?.last_sent_at && (
                  <div className="flex items-center gap-1 mt-1" data-testid={`recap-delivery-${r.recap_id}`}>
                    {r.delivery.sent_to_student && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] bg-teal-700 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        <Check className="h-2.5 w-2.5" /> student
                      </span>
                    )}
                    {r.delivery.sent_to_parent && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] bg-teal-700 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        <Check className="h-2.5 w-2.5" /> parent
                      </span>
                    )}
                    {r.delivery.last_status === 'failed' && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] bg-red-700 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        <AlertTriangle className="h-2.5 w-2.5" /> send failed
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => download(r)}
                className="h-8 px-3 rounded-lg bg-black text-white text-[10px] uppercase tracking-wider font-bold hover:bg-gray-800 flex items-center gap-1"
                data-testid={`recap-download-${r.recap_id}`}>
                <Download className="h-3 w-3" /> Download
              </button>
              {isTutor && (
                <button onClick={() => setSendingRecap(r)}
                  className="h-8 px-3 rounded-lg bg-red-700 hover:bg-red-800 text-white text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                  data-testid={`recap-send-${r.recap_id}`}>
                  <Send className="h-3 w-3" /> {r.delivery?.last_status === 'success' ? 'Resend' : 'Send'}
                </button>
              )}
              {isTutor && (
                <button onClick={() => remove(r)}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg border border-gray-200 hover:bg-red-50 text-red-700 flex items-center justify-center transition"
                  data-testid={`recap-delete-${r.recap_id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {open && isTutor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             onClick={closeModal} data-testid="recap-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-red-700" />
                <div className="text-sm font-bold uppercase tracking-wider">Generate Lesson Recap</div>
              </div>
              <button onClick={closeModal} disabled={generating || aiBusy}
                      className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                      data-testid="recap-modal-close"><X className="h-4 w-4" /></button>
            </div>

            <div className="px-5 py-4 space-y-5 overflow-y-auto flex-1">
              {/* Track selector */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 block">Lesson track</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {TRACKS.map((tr) => (
                    <button key={tr.key || 'general'} onClick={() => setTrack(tr.key)}
                      className={`h-9 px-3 rounded-lg text-[11px] uppercase tracking-wider font-bold transition ${track === tr.key ? 'bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                      data-testid={`recap-track-${(tr.key || 'general').toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
                      {tr.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-Summarise CTA */}
              <div className="rounded-xl border border-red-100 bg-gradient-to-br from-red-50/60 to-amber-50/40 p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-red-700 text-white flex items-center justify-center flex-shrink-0">
                      <Wand2 className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">Auto-Summarise Lesson</div>
                      <div className="text-[11px] text-gray-600">Winhouse AI reads the lesson notes, chat, math notes and board titles, then drafts every field below. You review and edit before generating.</div>
                    </div>
                  </div>
                  <button onClick={runAI} disabled={aiBusy || generating}
                    className="h-9 px-4 rounded-full bg-black hover:bg-gray-900 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 flex-shrink-0"
                    data-testid="recap-ai-summarise">
                    {aiBusy ? <><Loader2 className="h-3 w-3 animate-spin" /> Thinking…</> : <><Wand2 className="h-3 w-3" /> Auto-Summarise</>}
                  </button>
                </div>
                {aiMeta && (
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-2 flex items-center gap-2" data-testid="recap-ai-meta">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/80 text-[#D3D86F] font-bold">
                      <Sparkles className="h-2.5 w-2.5" /> Generated with Winhouse AI
                    </span>
                    <span>· context evidence {aiMeta.context_strength}/10</span>
                  </div>
                )}
                {contextWarning && (
                  <div className="flex items-start gap-2 mt-3 p-2 rounded-lg bg-amber-100/70 border border-amber-200" data-testid="recap-ai-warning">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-amber-900 leading-snug">{contextWarning}</div>
                  </div>
                )}
              </div>

              {/* Editable fields */}
              <FieldArea testid="recap-field-summary" label="Tutor's summary" hint="3–6 sentences. What happened, strengths, focus."
                value={draftSummary} onChange={(v) => { setDraftSummary(v); setTouched((t) => ({ ...t, summary: true })); }} rows={4} />
              <FieldArea testid="recap-field-homework" label="AI-suggested homework" hint="3–5 actionable tasks."
                value={aiHomework} onChange={(v) => { setAiHomework(v); setTouched((t) => ({ ...t, homework: true })); }} rows={4} />
              <FieldArea testid="recap-field-revision" label="AI revision notes" hint="Key revision bullets from the lesson."
                value={aiRevision} onChange={(v) => { setAiRevision(v); setTouched((t) => ({ ...t, revision: true })); }} rows={4} />
              <FieldArea testid="recap-field-next" label="Recommended plan for next lesson" hint="3–5 short bullets."
                value={aiNextPlan} onChange={(v) => { setAiNextPlan(v); setTouched((t) => ({ ...t, next: true })); }} rows={3} />
              <FieldArea testid="recap-field-parent" label="Parent progress report" hint="Warm 1-paragraph note for the parent."
                value={aiParent} onChange={(v) => { setAiParent(v); setTouched((t) => ({ ...t, parent: true })); }} rows={4} />

              <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
                We'll automatically include lesson notes, homework, math notes, document annotations, whiteboard snapshots and chat highlights. AI sections are clearly labelled "Generated with Winhouse AI" in the final PDF.
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button onClick={closeModal} disabled={generating || aiBusy}
                      className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider hover:bg-gray-100"
                      data-testid="recap-modal-cancel">Cancel</button>
              <button onClick={generate} disabled={generating || aiBusy}
                className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5"
                data-testid="recap-modal-submit">
                {generating ? <><Loader2 className="h-3 w-3 animate-spin" /> Building…</> : <><Sparkles className="h-3 w-3" /> Generate PDF</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <SendRecapModal
        open={!!sendingRecap}
        onClose={() => setSendingRecap(null)}
        recap={sendingRecap}
        roomId={roomId}
        token={token}
        onSent={(r) => {
          // Merge delivery into local list so badges update without refetching
          setRecaps((rs) => rs.map((x) => x.recap_id === sendingRecap?.recap_id ? { ...x, delivery: r.delivery } : x));
        }}
      />
    </div>
  );
};

const FieldArea = ({ label, hint, value, onChange, rows = 3, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 flex items-baseline justify-between gap-2">
      <span>{label}</span>
      {hint && <span className="text-gray-400 normal-case font-normal text-[10px]">{hint}</span>}
    </label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200 leading-relaxed"
      data-testid={testid} />
  </div>
);

export default RecapPanel;
